/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { gnoment } from 'shared-ui/gnoment';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import set from 'lodash/set';

import {
    NeedToKnow,
    ProgressIndicator,
    SecurityHelper,
    ScrollToHelper,
    CSRFacilityInformation,
    Box,
    Spacer,
    ContentBlock,
    Heading,
    Alert,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import { withRecaptchaFallback } from 'shared-ui/recaptcha';

import {
    OrderDetailsLayout,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';

import { updateAccount } from 'ui-account/src/actions/profileActions';
import { INVENTORY_PERMIT } from 'ui-field-sales/src/constants/types';

import { authModalActions } from 'shared-ui/auth/redux';
import GroupDetailHOC from '../../shared-components/registration/GroupDetailNew';
import AlternatePermitHolderEntry from '../../shared-components/registration/AlternatePermitHolderEntry';
import SimpleAlternatePermitHolderEntry from '../../shared-components/registration/AlternatePermitHolderNameEmailEntry';
import AlternatePermitHolderHOC from '../../shared-components/registration/AlternatePermitHolder';
import {
    GROUP_DETAIL_ADDRESS_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_ALT_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_COUNTRY_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_SAVE_PROFILE_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
} from '../../shared-components/constants/propPaths';
import * as sharedActions from '../../shared-components/actions/actions';
import { selectors as sharedSelectors } from '../../shared-components/reducers';
import {
    loadPermitMapping,
    permitContentSelector,
} from '../../shared-components/slices/permitContent';
import { getGateA } from '../../shared-components/utils/gateA';

import {
    scrollUp,
    getCurrentTime,
    isMobile,
    issuanceStatuses,
    getIds,
    getDivision,
    getLotteryPreferenceSelectionLimit,
    isDivisionOpen,
    runValidations,
    setLotteryButtonLabels,
    readRuleValue,
    readRuleAsMoment,
    loadFeesScript,
    generateLineItems,
    errorsToStringList,
    getNewId,
    verifyLotteryStatusMessages,
    getRecareaName,
    getValidationErrorStyle,
    hasRule,
    calculateRemainingIssuanceFee,
} from '../utils/utilsNew';
import {
    findNextAvailableDay,
    parsePermitSeasonInfo,
} from '../utils/availability-utils-scratch';
import ReservationDetail from '../components/registration/ReservationDetail';
import DailyLotteryPreference from '../components/DailyLotteryPreference';
import MultiDivisionPreferredDatesPicker from '../components/multi-division/PreferredDatesPicker';
import PermitHolderIdentification from '../components/registration/PermitHolderIdentification';
import EmergencyContact from '../components/registration/EmergencyContact';
import RecaptchaChallengeModal from '../components/RecaptchaChallengeModal';
import * as pActions from '../actions/permit';
import * as aActions from '../actions/availability';
import * as types from '../constants';
import * as recaptchaConstants from '../constants/recaptcha';
import {
    convertIssuanceToGroupDetailObj,
    convertIssuanceToGroupMembersObj,
} from '../utils/issuance-utilsNew';
import { ISSUANCE } from '../props/permit';

const PROGRESS_STEPS = ['Order Details', 'Review Cart', 'Payment', 'Confirmation'];

const dataBindings = {
    [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'groupLeader.home_address',
    [GROUP_DETAIL_PROP_PATH]: 'groupLeader',

    [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'address1',
    [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'address2',
    [GROUP_DETAIL_ALT_PROP_PATH]: 'altLeaders',
    [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
    [GROUP_DETAIL_COUNTRY_PROP_PATH]: 'country',
    [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
    [GROUP_DETAIL_PHONE_PROP_PATH]: 'cell_phone',
    [GROUP_DETAIL_SAVE_PROFILE_PROP_PATH]: 'save_profile',
    [GROUP_DETAIL_STATE_PROP_PATH]: 'state',
    [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'zip_code',
};

const GroupDetailNew = GroupDetailHOC(ISSUANCE, dataBindings);
const AlternatePermitHolder = AlternatePermitHolderHOC(
    AlternatePermitHolderEntry,
    ISSUANCE,
    dataBindings
);
const SimpleAlternatePermitHolder = AlternatePermitHolderHOC(
    SimpleAlternatePermitHolderEntry,
    ISSUANCE,
    dataBindings
);

const propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    permit: PropTypes.shape({
        id: PropTypes.string,
        org_code: PropTypes.string,
        name: PropTypes.string,
        recareas: PropTypes.arrayOf(PropTypes.string),
        components: PropTypes.shape({}),
        divisions: PropTypes.shape({}),
        validations: PropTypes.arrayOf(PropTypes.shape({})),
        rules: PropTypes.array,
        time_zone: PropTypes.string,
    }),
    verifyLottery: PropTypes.shape({
        lottery: PropTypes.shape({
            name: PropTypes.string,
        }),
        lottery_verify_status: PropTypes.number,
    }),
    recarea: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        })
    ),
    permitValidation: PropTypes.shape({}),
    issuance: PropTypes.shape({
        id: PropTypes.string,
        lottery_id: PropTypes.string,
        lottery_preferences: PropTypes.arrayOf(PropTypes.object),
        need_to_know_checked: PropTypes.bool,
    }),
    dispatch: PropTypes.func,
    lotteryError: PropTypes.shape({
        error: PropTypes.string,
    }),
    availabilityError: PropTypes.shape({}),
    availability: PropTypes.shape({}),
    fetchError: PropTypes.string,
    issuanceError: PropTypes.shape({
        error: PropTypes.string,
    }),
    updateIssuanceResult: PropTypes.string,
    updateIssuanceError: PropTypes.shape({
        error: PropTypes.string,
    }),
    updatedIssuance: PropTypes.shape({}),
    hasAccount: PropTypes.bool,
    forceLoad: PropTypes.bool,
    aggSeasonRules: PropTypes.array,
    permitMapping: PropTypes.shape({}),
    recaptcha: PropTypes.object.isRequired,
};

export class LotteryRegistrationPage extends React.Component {
    static propTypes = propTypes;

    constructor(props) {
        super(props);

        const accountFromSession = SecurityHelper.getAccount();

        this.state = {
            amountPaid: 0,
            groupDetail: {
                groupLeaderType: 'primary-leader',
                groupLeader: {
                    ...accountFromSession,
                    home_address: { ...accountFromSession.home_address },
                },
                otherLeader: {
                    email: '',
                },
                altLeaders: [],
            },
            groupMembers: [],
            emergencyContact: {
                first_name: '',
                last_name: '',
                phone: '',
            },
            division: {},
            selectedDates: [],
            needToKnowChecked: false,
            errors: [],
            validation: {},
            lotteryId: '',
            initializedPage: false,
            initializingPage: false,
            enableErrorValidation: false,
            lotteryInCart: false,
            verifyError: '',
            userRestrictedError: '',
            saveLeader: false,
            isDaily: false,
            isChallengeModalOpen: false,
            isSubmittingIssuance: false,
        };

        this.errorsRef = React.createRef();
    }

    componentDidMount() {
        const loggedin = SecurityHelper.isLoggedIn();
        if (!loggedin) {
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }
        this.initializePage();
    }

    componentDidUpdate(_, prevState) {
        if (
            !this.props.hasAccount ||
            this.state.initializedPage ||
            this.state.initializingPage ||
            prevState.verifyError === this.state.verifyError
        ) {
            return;
        }

        this.initializePage();
    }

    componentWillUnmount() {
        const { dispatch } = this.props;

        dispatch(pActions.clearError());
        dispatch(pActions.clearAvailabilityError());
        dispatch(pActions.clearFetchError());
        dispatch(pActions.clearUpdateIssuanceError());
        dispatch(pActions.clearIssuanceError());
    }

    getDivision() {
        if (!isEmpty(this.state.division) && isDivisionOpen(this.state.division.status)) {
            return this.state.division;
        }
        // user didn't select segment/zone
        return getDivision(this.props.permit.divisions) || new Map();
    }

    getFirstAvailableLotteryDay() {
        let initialLotteryDate = null;
        const bools = get(this.props, 'availability.bools');
        const { time_zone, rules } = this.props.permit;
        const currentTime = getCurrentTime(this.props);
        const blockNonLotteryDates = true;
        const blockLotteryDates = false;
        const permitSeasonInfo = parsePermitSeasonInfo(
            this.props.aggSeasonRules,
            time_zone,
            '',
            rules
        );

        if (bools && Object.keys(bools).length > 0) {
            initialLotteryDate = findNextAvailableDay(
                bools,
                permitSeasonInfo,
                blockNonLotteryDates,
                blockLotteryDates,
                currentTime
            );
        }
        initialLotteryDate =
            initialLotteryDate !== null ? gnoment(initialLotteryDate).utc() : currentTime;
        return initialLotteryDate;
    }

    setStartDate(firstPref) {
        const { preferred_date, preferred_range_start } = firstPref;

        if (!isEmpty(preferred_date) && gnoment(preferred_date).year() > 2000) {
            return preferred_date;
        }

        return preferred_range_start;
    }

    isPageLoading = () => {
        const { issuance, permit } = this.props;
        const { groupDetail, initializedPage } = this.state;

        if (isEmpty(groupDetail.groupLeader) || isEmpty(this.getDivision())) {
            return true;
        }

        const ids = getIds(this.props.router.params.reservationId);
        const newLoaded =
            ids.issuanceId === '' &&
            this.props.router.params.reservationId === `__${permit.id}`;
        const issuanceLoaded = ids.issuanceId !== '' && !!issuance && !!issuance.id;
        const issuanceMatched =
            issuanceLoaded &&
            this.props.router.params.reservationId === `${issuance.id}__${permit.id}`;
        const permitValidationsSet = !permit || !permit.validations;
        const newlyLoadedAndMatched = !newLoaded || !issuanceMatched;

        return (
            ((permitValidationsSet && newlyLoadedAndMatched) || !initializedPage) &&
            !this.props.forceLoad &&
            !this.props.verifyLottery
        );
    };

    async initializePage() {
        this.setState({ initializingPage: true, initializedPage: false });
        const ids = getIds(this.props.router.params.reservationId);
        const permitId = this.props.router.params.permitId;
        const paramLotteryId = this.props.router.params.lotteryId;
        const issuanceId = ids.issuanceId;
        let group = {
            ...this.state.groupDetail,
            groupLeader: SecurityHelper.getAccount(),
        };

        const lotteryAndPermitApiCalls = [
            this.props.dispatch(pActions.verifyLottery(permitId, paramLotteryId)),
            this.props.dispatch(pActions.fetchPermit(permitId)),
            this.props.dispatch(sharedActions.fetchReservationSummaryImageUrl(permitId)),
        ];
        Promise.all(lotteryAndPermitApiCalls).then(() => {
            const lotteryId = get(this.props, 'verifyLottery.lottery.id');
            const lotteryStatus = get(this.props, 'verifyLottery.lottery_verify_status');
            if (lotteryId) {
                this.setState({ lotteryId });
            }

            if (lotteryStatus === types.VERIFY_LOTTERY_STATUSES.EXISTING_LOTTERY) {
                if (!issuanceId) {
                    this.setState({
                        verifyError: verifyLotteryStatusMessages.get(lotteryStatus),
                    });
                }
            } else if (lotteryStatus === types.VERIFY_LOTTERY_STATUSES.USER_RESTRICTED) {
                this.setState({
                    userRestrictedError: verifyLotteryStatusMessages.get(lotteryStatus),
                });
            } else if (
                lotteryStatus !== types.VERIFY_LOTTERY_STATUSES.LOTTERY_AVAILABLE
            ) {
                this.setState({
                    verifyError: verifyLotteryStatusMessages.get(lotteryStatus),
                });
            }

            const lotteries = get(this.props, 'permit.lotteries', false);
            if (lotteries) {
                // this should return one daily lottery if found which we then use to set single preference date with
                const dailyLottery = filter(lotteries, { id: lotteryId, is_daily: true });
                if (dailyLottery.length) {
                    const dailyStartTimestamp = get(
                        this.props,
                        'verifyLottery.lottery.inventory_info.dates.start.seconds',
                        false
                    );
                    if (dailyStartTimestamp) {
                        this.setState({ isDaily: true });
                    } else {
                        this.setState({
                            verifyError: 'Daily lottery not open for this time period',
                        });
                    }
                }
            }
        });

        await loadFeesScript();

        const apiCalls = [
            this.props.dispatch(
                pActions.fetchAggregateSeason(this.props.router.params.permitId, true)
            ),
        ];

        await Promise.all(apiCalls);

        const initDivision = this.getDivision();
        if (!isEmpty(initDivision)) {
            this.setState({ division: initDivision });
            this.props.dispatch(
                aActions.fetchAvailability(
                    permitId,
                    initDivision.id,
                    readRuleAsMoment(
                        'SeasonStart',
                        this.props.aggSeasonRules,
                        this.props.permit.time_zone,
                        initDivision.id,
                        false
                    ),
                    readRuleAsMoment(
                        'SeasonEnd',
                        this.props.aggSeasonRules,
                        this.props.permit.time_zone,
                        initDivision.id,
                        false
                    ),
                    false,
                    true
                )
            );
        }

        this.props.dispatch(loadPermitMapping());

        const recId = get(this.props, 'permit.recareas[0]');
        if (recId) {
            this.props.dispatch(pActions.fetchRecarea(recId));
        }

        if (issuanceId && issuanceId !== '') {
            this.props
                .dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId))
                .then(() => {
                    const issuance = this.props.issuance;
                    if (!get(issuance, 'permit_holder.first_name')) {
                        // Since we don't have permit holder info already set on the issuance, grab it from the session account
                        const accountFromSession = SecurityHelper.getAccount();
                        issuance.permit_holder = {
                            first_name: get(accountFromSession, 'first_name'),
                            last_name: get(accountFromSession, 'last_name'),
                            email: get(accountFromSession, 'email'),
                            phone: get(accountFromSession, 'cell_phone'),
                        };
                        const address = accountFromSession.home_address || {};
                        issuance.permit_holder_address = {
                            street_address1: get(address, 'address1'),
                            street_address2: get(address, 'address2'),
                            city: get(address, 'city'),
                            state_code: get(address, 'state'),
                            postal_code: get(address, 'zip_code'),
                        };
                    }
                    this.handleGroupMemberChange(
                        convertIssuanceToGroupMembersObj(issuance.group_info)
                    );
                    this.handleEmergencyContactChange(issuance.emergency_contact);
                    group = convertIssuanceToGroupDetailObj(
                        issuance,
                        this.state.groupDetail
                    );

                    const preferences = this.props.issuance.lottery_preferences.map((d) =>
                        cloneDeep(d)
                    );
                    for (const i in preferences) {
                        if (Object.hasOwn(preferences, i)) {
                            const prefDate = preferences[i].preferred_date;
                            preferences[i].preferred_date = gnoment.utc(prefDate);
                            preferences[i].preferred_range_start = gnoment.utc(
                                preferences[i].preferred_range_start
                            );
                            preferences[i].preferred_range_end = gnoment.utc(
                                preferences[i].preferred_range_end
                            );
                            preferences[i].id = getNewId();
                        }
                    }
                    this.setState({
                        groupDetail: group,
                        selectedDates: preferences,
                        needToKnowChecked: this.props.issuance.need_to_know_checked,
                        lotteryId: this.props.issuance.lottery_id,
                        amountPaid: get(this.props.issuance, 'amount_paid'),
                    });
                })
                .catch(() => {
                    // avoid throwing error up
                    // reservation failed to load, will be handled by reducer GET_ISSUANCE_ERROR
                });
        } else {
            this.props.dispatch(pActions.clearIssuanceFromStore());
        }
        this.setState({
            groupDetail: group,
            initializingPage: false,
            initializedPage: true,
        });
    }

    handleNeedToKnowCheckboxChange = () => {
        this.setState(
            (prevState) => ({
                needToKnowChecked: !prevState.needToKnowChecked,
                enableErrorValidation: true,
            }),
            () => {
                if (this.state.needToKnowChecked) {
                    this.isValidToCheckout();
                    if (!isMobile.any()) {
                        scrollUp();
                    }
                }
            }
        );
    };

    handleGroupMemberChange = (groupMembers) => this.setState({ groupMembers });

    handleEmergencyContactChange = (emergencyContact) =>
        this.setState({ emergencyContact });

    fetchGroupDetails() {
        const groupDetailInfo = {};
        if (this.state.groupDetail.groupLeaderType === 'primary-leader') {
            groupDetailInfo.user_id = SecurityHelper.getAccount().account_id;
        }
        const altGroupLeaders = [];
        if (this.state.groupDetail.altLeaders) {
            for (
                let index = 0;
                index < this.state.groupDetail.altLeaders.length;
                index++
            ) {
                const leader = this.state.groupDetail.altLeaders[index];

                // Do not create alternate group leader if email, first name, and last name are all blank
                // This happens when users click on "Add alternate permit holder" button but not enter any info in the form control.
                if (!leader?.email && !leader?.first_name && !leader?.last_name) continue;

                altGroupLeaders.push({
                    email: get(leader, 'email', ''),
                    first_name: get(leader, 'first_name', ''),
                    last_name: get(leader, 'last_name', ''),
                    is_verified: get(leader, 'is_verified', false),
                    is_declined: get(leader, 'is_declined', false),
                });
            }
        }
        groupDetailInfo.alt_group_leaders = altGroupLeaders;
        return groupDetailInfo;
    }

    fetchPermitHolder() {
        // returns an object with keys 'permit_holder' and 'permit_holder_address'
        // for use when sending issuance object away
        const permitHolderInfo = {};
        if (this.state.groupDetail.groupLeaderType === 'primary-leader') {
            const {
                first_name,
                last_name,
                email,
                cell_phone: phone,
            } = get(this.state.groupDetail, 'groupLeader', {});
            const homeAddress = get(
                this.state.groupDetail,
                'groupLeader.home_address',
                {}
            );
            const {
                address1: street_address1,
                address2: street_address2,
                city,
                state: state_code,
                zip_code: postal_code,
                country: country_code,
            } = homeAddress;
            permitHolderInfo.permit_holder = {
                ...get(this.props.issuance, 'permit_holder', {}),
                first_name,
                last_name,
                email,
                phone,
            };
            permitHolderInfo.permit_holder_address = {
                street_address1,
                street_address2,
                city,
                state_code,
                postal_code,
                country_code,
            };
        }
        return permitHolderInfo;
    }

    fetchLotteryPreferences() {
        const results = {
            lottery_preferences: [],
        };
        this.state.selectedDates.forEach((d) => {
            results.lottery_preferences.push(d);
        });
        return results;
    }

    fetchMembers() {
        const memberInfo = {
            group_info: [],
        };
        if (this.state.isDaily) {
            if (this.state.groupMembers.length > 0) {
                memberInfo.group_info.push({
                    date_of_birth: this.state.groupMembers[0].birthdate,
                });
            }
        }
        return memberInfo;
    }

    focusErrorNotification = () => {
        if (this.errorsRef.current) {
            this.errorsRef.current.focus();
        }
    };

    isValidToCheckout() {
        const validation = { ...this.props.permitValidation };
        const summary = [];
        Object.keys(validation).forEach((key) => {
            summary.push(validation[key]);
        });
        this.setState({ errors: summary, validation });
        return summary.length === 0;
    }

    saveLeaderInfo() {
        const acct = this.createUpdatedAccountObject(SecurityHelper.getAccount());
        this.props.dispatch(updateAccount(acct));
    }

    createUpdatedAccountObject(prevAcct) {
        const { first_name, last_name, home_address, email, cell_phone } =
            this.state.groupDetail.groupLeader;
        return { ...prevAcct, first_name, last_name, home_address, email, cell_phone };
    }

    addToCart = async ({ navigate = true } = {}) => {
        const { lotteryInCart, lotteryNotActiveError, saveLeader } = this.state;

        if (this.state.verifyError || this.state.userRestrictedError) {
            return;
        }

        if (!lotteryInCart && !lotteryNotActiveError && this.isValidToCheckout()) {
            if (saveLeader) {
                this.saveLeaderInfo();
            }

            const item = this.createIssuanceObject();

            // new item
            if (!getIds(this.props.router.params.reservationId).issuanceId) {
                if (
                    this.props.recaptcha.isChallengeRequired &&
                    !this.state.isChallengeModalOpen
                ) {
                    this.setState({ isChallengeModalOpen: true });
                    return false;
                }

                if (this.state.isSubmittingIssuance) {
                    return false;
                }

                const recaptcha = await getGateA(
                    this.props.recaptcha,
                    recaptchaConstants.CH_LOTTERY_REGISTRATION
                );

                this.setState({ isSubmittingIssuance: true });

                try {
                    await this.props.dispatch(
                        pActions.addItem(types.LOTTERY, item, true, recaptcha)
                    );
                    this.props.recaptcha.resetChallenge();
                    this.setState({
                        isChallengeModalOpen: false,
                        isSubmittingIssuance: false,
                    });
                    if (navigate) window.location.assign('/cart');
                    return true;
                } catch (err) {
                    const { shouldRetry } =
                        this.props.recaptcha.handleRecaptchaError(err);
                    if (shouldRetry) {
                        this.setState({
                            isChallengeModalOpen: true,
                            isSubmittingIssuance: false,
                        });
                        ScrollToHelper.scrollTo(0, 0);
                        return false;
                    }
                    if (
                        !err?.isAxiosError ||
                        (err?.response?.status !== 400 && err?.response?.status !== 500)
                    ) {
                        console.error(
                            `Failed on applying to lottery ${this.props.router.params.lotteryId} for permit ${this.props.router.params.permitId}`
                        );
                    }
                    this.setState({ isSubmittingIssuance: false });
                    ScrollToHelper.scrollTo(0, 0);
                }
            }
            // modify existing item
            else {
                await this.props.dispatch(pActions.updateIssuance(item));

                if (
                    this.props.updateIssuanceResult ===
                    types.ISSUANCE_UPDATE_REQUIRES_PAYMENT
                ) {
                    if (navigate) window.location.assign('/cart');
                    return true;
                }
                if (
                    this.props.updateIssuanceResult === types.ISSUANCE_UPDATE_SUCCESSFUL
                ) {
                    this.props.dispatch(
                        pActions.bypassToOrderConfirmation(
                            this.props.updatedIssuance,
                            this.props.permit
                        )
                    );
                    if (navigate) {
                        this.props.router.navigate(
                            `/permits/${this.props.permit.id}/confirmation`
                        );
                    }
                    return true;
                }
            }
        } else if (!this.state.enableErrorValidation) {
            this.focusErrorNotification();
            this.setState(() => ({ enableErrorValidation: true }), scrollUp);
        } else {
            this.focusErrorNotification();
            scrollUp();
        }
    };

    createIssuanceObject() {
        try {
            const ids = getIds(this.props.router.params.reservationId);
            const issuanceId = ids.issuanceId;
            const startDate = this.setStartDate(get(this.state.selectedDates, ['0']));

            const initItem = {
                id: issuanceId,
                group_info: [],
                emergency_contact: this.state.emergencyContact,
                user_id: SecurityHelper.getAccount().account_id,
                permit_id: this.props.permit.id,
                division_id: this.getDivision().id,
                start_date: startDate,
                status: get(this.props.issuance, 'status')
                    ? this.props.issuance.status
                    : issuanceStatuses.get(types.LOTTERY_IN_PROGRESS),
                need_to_know_checked: this.state.needToKnowChecked,
                lottery_id: this.state.lotteryId,
                user_type: 1,
                acquisition_type: 2,
            };
            const groupDetail = this.fetchGroupDetails();
            const permitHolder = this.fetchPermitHolder();
            const groupMembers = this.fetchMembers();
            const lotteryDetail = this.fetchLotteryPreferences();
            if (this.props.issuance) {
                initItem.id = this.props.issuance.id;
            }
            return Object.assign(
                initItem,
                groupDetail,
                permitHolder,
                lotteryDetail,
                groupMembers
            );
        } catch (e) {
            // errors are generated on page load due to some values initially not being populated
            // e.g. division
            return {};
        }
    }

    removeFromCart = () => {
        const issuanceId = get(this.props.issuance, 'id');
        const permitId = get(this.props.permit, 'id');
        if (issuanceId && permitId) {
            const reservationId = `${issuanceId}${types.RES_ID_DELIMITER}${permitId}`;
            this.props.dispatch(
                pActions.cancelReservation(reservationId, this.props.router.navigate)
            );
        } else {
            window.location.assign(`/permits/${permitId}`);
        }
    };

    renderMultiDivisionLotteryPreferencePicker() {
        const maxPreferences = getLotteryPreferenceSelectionLimit(
            this.props.permit,
            undefined
        );

        return (
            <MultiDivisionPreferredDatesPicker
                allowSameDate
                permit={this.props.permit}
                aggSeasonRules={this.props.aggSeasonRules}
                divisions={this.props.permit ? this.props.permit.divisions : {}}
                seasonStart={this.getFirstAvailableLotteryDay()}
                dates={this.state.selectedDates}
                validation={this.state.validation}
                onChange={(d) =>
                    this.setState(
                        () => ({ selectedDates: d }),
                        () => {
                            if (this.state.enableErrorValidation) {
                                this.isValidToCheckout();
                            }
                        }
                    )
                }
                max={this.props.permit && maxPreferences ? maxPreferences : null}
                isCommercialUser={SecurityHelper.isCommercialAccount()}
                isLottery
            />
        );
    }

    renderDailyLotteryPreference() {
        const maxGroupSize = readRuleValue('MaxGroupSize', this.props.permit.rules);
        const dailyStartTimestamp = get(
            this.props,
            'verifyLottery.lottery.inventory_info.dates.start.seconds',
            false
        );
        const maxPreferences = getLotteryPreferenceSelectionLimit(
            this.props.permit,
            undefined
        );

        const validationErrors = {
            groupMaxRequired: getValidationErrorStyle(
                this.state.validation,
                `Choice #1: ${types.IS_GROUP_MAX_EMPTY}`
            ),
        };

        return (
            <DailyLotteryPreference
                permitId={this.props.permit.id}
                selectedDates={this.state.selectedDates}
                maxGroupSize={maxGroupSize}
                selectedStartDate={gnoment.unix(dailyStartTimestamp).utc()}
                maxPreferences={maxPreferences}
                onChange={(d) =>
                    this.setState(
                        () => ({ selectedDates: d }),
                        () => {
                            if (this.state.enableErrorValidation) {
                                this.isValidToCheckout();
                            }
                        }
                    )
                }
                divisions={this.props.permit.divisions}
                validationErrors={validationErrors}
            />
        );
    }

    renderComponents(permitID) {
        switch (permitID) {
            case types.DENALI_ID:
                return [this.renderEmergencyContactInformation()];
            default:
                return null;
        }
    }

    renderEmergencyContactInformation() {
        return (
            <EmergencyContact
                key="emergency-contact"
                handleEmergencyContactChange={this.handleEmergencyContactChange}
                validation={this.state.validation}
                contact={this.state.emergencyContact}
            />
        );
    }

    renderBirthDate() {
        const showIfTrue = this.state.isDaily; // for extensibility
        if (get(this.props, 'permit.components.permit_holder_birthdate') && showIfTrue) {
            return (
                <PermitHolderIdentification
                    groupMembers={this.state.groupMembers}
                    handleGroupMemberChange={this.handleGroupMemberChange}
                    collectBirthdate
                />
            );
        }
        return null;
    }

    updateStateGroupDetail = (id, path, value) =>
        this.setState(({ groupDetail }) => ({
            groupDetail: set({ ...groupDetail }, path, value),
        }));

    shouldRenderAlternatePlaceHolders = () =>
        !!get(this.props.permit, 'components.alternate_group_leader') &&
        (!get(this.state, 'isDaily') ||
            types.ANGELS_LANDING_FACILITIES.includes(this.props.permit.id)); // only Angels Landing permits show APH for daily lottery application

    renderAlternatePlaceHolders() {
        const { issuance, permit } = this.props;
        const { groupDetail } = this.state;

        // Create custom data bindings for core permits specific paths
        const paths = {
            email: 'email',
            last_name: 'last_name',
            first_name: 'first_name',
            alt_group_detail: dataBindings[GROUP_DETAIL_ALT_PROP_PATH],
        };

        return hasRule('SimpleAlternateLeader', permit.rules, '')
            ? this.renderSimpleAlternatePermitHolders(
                  issuance,
                  permit,
                  groupDetail,
                  false,
                  paths
              )
            : this.renderAlternatePermitHolders(
                  issuance,
                  permit,
                  groupDetail,
                  false,
                  paths
              );
    }

    renderAlternatePermitHolders = (issuance, permit, groupDetail, disabled, paths) => {
        const alternateModalSections = [
            {
                title: 'Why An Alternate Permit Holder?',
                paragraph:
                    'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.',
            },
            {
                title: 'How to Add an Alternate Permit Holder',
                paragraph: (
                    <>
                        Enter an email address for an alternate permit holder.The
                        alternate permit holder must respond and accept being an alternate
                        within <strong> 72 hours </strong>
                        or this permit will not have an alternate.If the alternate does
                        not have a recreation.gov account, they will be required to create
                        an account.
                    </>
                ),
            },
            {
                title: 'Alternate Permit Holder Considerations',
                paragraph:
                    'Being an alternate carries the same responsibilities as the primary permit holder. If there are seasonal reservations restrictions, these will apply to the alternate as if they are the primary permit holder. In other words, an alternate may not be able to reserve another permit or be an alternate for another permit at this facility for this season.',
            },
        ];

        return (
            <AlternatePermitHolder
                alternateMessageDetails={
                    <>
                        Please enter a valid email address for each of the intended
                        holders. They will receive an email and will need to validate
                        within <strong>72 hours</strong>.
                    </>
                }
                alternateModalSections={alternateModalSections}
                displayValidationError
                hasPermission
                isInternal={false}
                isReadOnly={disabled}
                pActions={{
                    ...pActions,
                    formValidationChange: Function.prototype,
                    updatePendingReservation: this.updateStateGroupDetail,
                }}
                permit={permit}
                reservation={{
                    ...groupDetail,
                    id: this.props.router.params.reservationId,
                    sales_channel_type: 0,
                    status: get(
                        issuance,
                        'status',
                        issuanceStatuses.get(types.LOTTERY_IN_PROGRESS)
                    ),
                }}
                validation={this.state.phValidations}
                paths={paths}
            />
        );
    };

    setValidations = (path, error) => {
        this.props.dispatch(pActions.formValidationChange(path, error));
    };

    renderSimpleAlternatePermitHolders = (
        issuance,
        permit,
        groupDetail,
        disabled,
        paths
    ) => {
        const alternateModalSections = [
            {
                title: 'Why An Alternate Permit Holder?',
                paragraph:
                    'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.',
            },
            {
                title: 'How to Add an Alternate Permit Holder',
                paragraph: <p>Enter a name for an alternate permit holder.</p>,
            },
        ];

        return (
            <SimpleAlternatePermitHolder
                alternateMessageDetails={
                    <p>Please enter a name for each of the intended holders.</p>
                }
                alternateModalSections={alternateModalSections}
                displayValidationError
                hasPermission
                isInternal={false}
                isReadOnly={disabled}
                pActions={{
                    ...pActions,
                    formValidationChange: this.setValidations,
                    updatePendingReservation: this.updateStateGroupDetail,
                }}
                permit={permit}
                reservation={{
                    ...groupDetail,
                    id: this.props.router.params.reservationId,
                    sales_channel_type: 0,
                    status: get(
                        issuance,
                        'status',
                        issuanceStatuses.get(types.LOTTERY_IN_PROGRESS)
                    ),
                }}
                validation={this.state.phValidations}
                paths={paths}
            />
        );
    };

    renderGroupDetail() {
        const { groupDetail } = this.state;

        return (
            <GroupDetailNew
                displayValidationError
                hasPermission
                pActions={{
                    ...pActions,
                    formValidationChange: Function.prototype,
                    updatePendingReservation: this.updateStateGroupDetail,
                }}
                reservation={{
                    ...groupDetail,
                    sales_channel_type: 0,
                    id: this.props.router.params.reservationId,
                }}
                validation={this.state.phValidations}
            />
        );
    }

    render() {
        const registrationValidation = runValidations(
            this.props.dispatch,
            this.props.permit.validations,
            this.state,
            types.REGISTRATION
        );
        const lotteryRegistrationValidation = runValidations(
            this.props.dispatch,
            this.props.permit.validations,
            this.state,
            types.LOTTERY_REGISTRATION
        );
        let preferenceValidation = [];
        if (this.state.selectedDates) {
            for (let i = 0; i < this.state.selectedDates.length; i += 1) {
                const choiceIndex = `Choice #${i + 1}`;
                preferenceValidation = preferenceValidation.concat(
                    runValidations(
                        this.props.dispatch,
                        this.props.permit.validations,
                        this.state.selectedDates[i],
                        types.LOTTERY_PREFERENCE,
                        choiceIndex,
                        this.state.selectedDates[i]?.isInvalid
                    )
                );
            }
        }

        let lotteryError = '';
        if (this.props.lotteryError) {
            lotteryError = this.props.lotteryError;
        } else if (this.state.verifyError) {
            lotteryError = this.state.verifyError;
        }

        const errorsList = errorsToStringList([
            lotteryError,
            this.state.userRestrictedError,
            this.props.availabilityError,
            this.props.fetchError,
            this.state.lotteryNotActiveError,
            this.props.updateIssuanceError,
            this.props.issuanceError,
        ]);

        const division = this.getDivision();
        const isCommercial = SecurityHelper.isCommercialAccount();
        const commercialText = isCommercial ? ` (${types.COMMERCIAL})` : '';

        const lineItems = generateLineItems(
            this.createIssuanceObject(),
            division,
            this.props.permit
        );
        const buttonLabels = setLotteryButtonLabels(
            this.props.issuance,
            calculateRemainingIssuanceFee(lineItems, this.state.amountPaid)
        );

        const summary = (
            <OrderDetailsSummary
                isLoading={this.isPageLoading()}
                heading={`${division.name}${commercialText}`}
                imageUrl={this.props.imageUrl}
                parentName={this.props.permit.name}
                onProceedToCartClick={(opts) => this.addToCart(opts)}
                proceedToCartButtonText={buttonLabels.proceed}
                secondaryButtonText={buttonLabels.continue}
                onSecondaryButtonClick={() => this.addToCart()}
                showBuyNow
                reservationId={this.props.router.params.reservationId}
            >
                <ReservationDetail
                    lineItems={lineItems}
                    name={get(this.props, 'verifyLottery.lottery.name', '')}
                    permit={this.props.permit}
                    division={division}
                    recareaName={getRecareaName(this.props.recarea)}
                    issuanceStatus={
                        get(this.props.issuance, 'status')
                            ? this.props.issuance.status
                            : issuanceStatuses.get(types.LOTTERY_IN_PROGRESS)
                    }
                    selectedDates={this.state.selectedDates}
                    isLottery
                    isCommercialUser={isCommercial}
                    buttonLabels={buttonLabels}
                    amountPaid={this.state.amountPaid}
                    isHighUseFeeApplicable={false}
                    reservationDetailName={this.props.permit.name}
                    permitMapping={this.props.permitMapping}
                />
            </OrderDetailsSummary>
        );

        const errorBulletList = (
            <ul>
                {this.state.errors.map((err) => {
                    let newErr = err;
                    if (err.toString().includes(types.ERROR_CLAIM_TOKEN)) {
                        newErr = types.ERROR_TOKEN_GENERAL;
                    }
                    return <li key={getNewId()}>{newErr}</li>;
                })}
                {errorsList.map((err) => {
                    let newErr = err;
                    if (err.toString().includes(types.ERROR_CLAIM_TOKEN)) {
                        newErr = types.ERROR_TOKEN_GENERAL``;
                    }
                    return <li key={getNewId()}>{newErr}</li>;
                })}
            </ul>
        );

        /* Fire the view_item GA Event */
        <SendGAEvent
            eventType="add_to_cart"
            itemCategory={INVENTORY_PERMIT}
            facilityId={this.props.permit.id}
            facilityName={this.props.permit.name}
            isDataLoading={!this.props.permit || !this.props.permit.id}
        />;

        return (
            <div className="rec-order-detail-page per-registration-page">
                <Box background="gray-2" paddingY="sm" tag="section">
                    <ProgressIndicator steps={PROGRESS_STEPS} activeStep={1} />
                </Box>
                <Spacer size="xl" />
                <ContentBlock>
                    <OrderDetailsLayout summaryElement={summary}>
                        {registrationValidation.concat(
                            lotteryRegistrationValidation,
                            preferenceValidation
                        )}
                        <div
                            tabIndex="-1"
                            style={{ outline: 'none' }}
                            ref={this.errorsRef}
                            aria-live="polite"
                        >
                            {!(isEmpty(this.state.errors) && isEmpty(errorsList)) && (
                                <Alert
                                    className="permit-error-notification"
                                    type="error"
                                    key={getNewId()}
                                    heading={
                                        this.state.userRestrictedError
                                            ? 'User Restricted'
                                            : "We're working to get this reservation booked for you, but there is an issue with some necessary information. Please take a moment to update the information flagged below and try again."
                                    }
                                    hasAriaLive
                                >
                                    {errorBulletList}
                                </Alert>
                            )}
                        </div>
                        <Heading headingLevel={1} appearance="h3">
                            Lottery Registration
                        </Heading>
                        <CSRFacilityInformation
                            inventory="Permit"
                            facility_id={this.props.permit.id}
                        />
                        <OrderDetailsSection
                            heading={`${this.props.permit.name}${commercialText}`}
                            onRemoveClick={this.removeFromCart}
                            isLoading={this.isPageLoading()}
                        >
                            {!this.state.verifyError &&
                                !this.state.userRestrictedError && (
                                    <form
                                        id="lottery-registration"
                                        className="rec-order-detail-section"
                                    >
                                        {this.renderGroupDetail()}
                                        {this.shouldRenderAlternatePlaceHolders() &&
                                            this.renderAlternatePlaceHolders()}
                                        {this.renderComponents(this.props.permit.id)}
                                        {this.renderBirthDate()}
                                        {this.state.isDaily
                                            ? this.renderDailyLotteryPreference()
                                            : this.renderMultiDivisionLotteryPreferencePicker()}
                                        <NeedToKnow
                                            siteName={this.props.permit.name}
                                            siteInfo={get(
                                                this.props.permit,
                                                'description.need_to_know_order'
                                            )}
                                            isChecked={this.state.needToKnowChecked}
                                            onChange={this.handleNeedToKnowCheckboxChange}
                                        />
                                    </form>
                                )}
                        </OrderDetailsSection>
                    </OrderDetailsLayout>
                </ContentBlock>
                <RecaptchaChallengeModal
                    isOpen={this.state.isChallengeModalOpen}
                    onClose={() => this.setState({ isChallengeModalOpen: false })}
                    onContinue={() => this.addToCart()}
                    recaptcha={this.props.recaptcha}
                    isSubmitting={this.state.isSubmittingIssuance}
                    action={recaptchaConstants.CH_LOTTERY_REGISTRATION}
                />
            </div>
        );
    }
}

function select(state) {
    return {
        permit: state.permit.result,
        verifyLottery: state.permit.verify_lottery,
        availability: state.permit.availability,
        issuance: state.permit.issuance,
        recarea: state.permit.recarea,
        issuanceError: state.permit.issuanceError,
        updateIssuanceResult: state.permit.update_issuance_result,
        updateIssuanceError: state.permit.update_issuance_error,
        updatedIssuance: state.permit.updated_issuance,
        fetchError: state.permit.fetch_err,
        currentDate: state.permit.current_date,
        lotteryError: state.permit.error,
        availabilityError: state.permit.availability_err,
        permitValidation: state.permitValidation.results,
        hasAccount: SecurityHelper.isLoggedIn(),
        aggSeasonRules: state.permit.agg_season_rules,
        imageUrl: sharedSelectors.getImageUrlSelector(state),
        permitMapping: permitContentSelector.permitMapping(state),
    };
}

export default withCompatRouter(
    connect(select)(withRecaptchaFallback(LotteryRegistrationPage))
);
